import { createFeatureSelector, createSelector } from '@ngrx/store';
import { UsersState } from '../states/users.state';

export const selectUsersState = createFeatureSelector<UsersState>('users');

export const selectUser = createSelector(selectUsersState, (state) => state.user);

export const selectAllUsers = createSelector(selectUsersState, (state) => state.users);

export const selectUsersLoading = createSelector(selectUsersState, (state) => state.loading);

export const selectUserIsSaving = createSelector(selectUsersState, (state) => state.isSaving);

export const selectUserSavingError = createSelector(selectUsersState, (state) => state.savingError);
export const selectUsersError = createSelector(selectUsersState, (state) => state.error);

export const selectSearchForm = createSelector(
  selectUsersState,
  (state: UsersState) => state.query
);

export const selectUserSort = createSelector(selectUsersState, (state) => state.sort);

export const selectUserPagination = createSelector(selectUsersState, (state) => state.pagination);

export const selectSortedUsers = createSelector(selectAllUsers, selectUserSort, (users, sort) => {
  if (!users || !sort.active) {
    return users;
  }

  const sorted = [...users];

  sorted.sort((a, b) => {
    const valueA = a[sort.active!];
    const valueB = b[sort.active!];
    const directionFactor = sort.direction === 'asc' ? 1 : -1;
    // Date
    if (sort.active === 'createdAt') {
      return (
        directionFactor * (new Date(valueA as Date).getTime() - new Date(valueB as Date).getTime())
      );
    }

    // String
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return directionFactor * valueA.localeCompare(valueB);
    }

    // Number (fallback)
    return directionFactor * (Number(valueA) - Number(valueB));
  });

  return sorted;
});

export const selectPagedUsers = createSelector(
  selectSortedUsers,
  selectUserPagination,
  (users, pagination) => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;

    return users?.slice(start, end);
  }
);

export const selectVisibleUserPages = createSelector(
  selectUserPagination,
  selectAllUsers,
  (pagination, totalUsers): number[] => {
    const { pageIndex, pageSize } = pagination;
    const totalUserCount = totalUsers?.length ?? 0;
    const totalPages = Math.ceil(totalUserCount / pageSize);

    const maxVisible = 5;
    const currentPage = pageIndex + 1;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisible / 2);

    let start = currentPage - half;
    let end = start + maxVisible - 1;

    // Clamp to boundaries
    if (start < 1) {
      start = 1;
      end = maxVisible;
    }

    if (end > totalPages) {
      end = totalPages;
      start = totalPages - maxVisible + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
);

export const selectIsUserMapView = createSelector(selectUsersState, (state) => state.isUserMapView);

export const selectTotalUsers = createSelector(selectAllUsers, (users) => users?.length);
